import type { RecordFileItem } from '@ofmos/shared/files';
import { useState } from 'react';
import { downloadBlob } from '../../app/csv/download';
import { useAppClient } from '../../data/ClientProvider';
import { useToast } from '../../ui';

/**
 * DOWNLOADING ONE ATTACHMENT, IN THE ONE PLACE BOTH SHAPES READ IT FROM.
 *
 * THE DOWNLOAD GOES THROUGH THE TRANSPORT, never through an `<a download>`. That is
 * `app/csv/download.ts`'s rule applied to bytes: a bare link leaves the transport, so a 403
 * renders the error envelope as a page and the surface that asked cannot report the refusal.
 *
 * It became a hook when the phone sheet moved out of its row (wave 1c): the desktop row and
 * the sheet had one copy each, and the move would have made a third. The three lines that
 * differ between them - the button's variant, its width, where it sits - are layout and stay
 * in the components; the busy flag, the transport call and the refusal sentence are the same
 * behaviour twice and belong here.
 */
export interface FileDownload {
  readonly busy: boolean;
  readonly download: () => void;
}

export function useFileDownload(item: RecordFileItem): FileDownload {
  const client = useAppClient();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      downloadBlob(item.label, await client.files.download(item.fileId));
    } catch (error) {
      toast({
        tone: 'danger',
        title: 'That file could not be downloaded. You may no longer have access to this record.',
        description: error instanceof Error ? error.message : undefined,
      });
    } finally {
      setBusy(false);
    }
  };

  return { busy, download: () => void run() };
}
